import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { api, unwrap, apiDownloadRich } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import StatCard from '@/components/match/board/StatCard';

const BASE = `${API_URL}/api/match/audit-trail`;
const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day:   'numeric',
      year:  'numeric',
      hour:  'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtPct(n) {
  if (n == null) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function fmtConf(n) {
  if (n == null) return '—';
  return `${Math.round(n)}%`;
}

function buildQs(filters, extra = {}) {
  const p = new URLSearchParams();
  if (filters.session_id)  p.set('session_id',  filters.session_id);
  if (filters.provider)    p.set('provider',    filters.provider);
  if (filters.outcome)     p.set('outcome',     filters.outcome);
  if (filters.date_from)   p.set('date_from',   filters.date_from);
  if (filters.date_to)     p.set('date_to',     filters.date_to);
  Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)));
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Outcome chip
// ---------------------------------------------------------------------------

function OutcomeChip({ outcome }) {
  const cls = {
    confirmed:   'bg-[var(--boss-success-soft)] text-[var(--boss-success-text)] border-[var(--boss-success-border)]',
    overridden:  'bg-[var(--boss-warning-soft)] text-[var(--boss-warning-text)] border-[var(--boss-warning-border)]',
    auto_booked: 'bg-[var(--boss-info-soft)]    text-[var(--boss-info-text)]    border-[var(--boss-info-border)]',
  }[outcome] || 'bg-boss-elevated text-boss-text-muted border-boss-border';

  const label = {
    confirmed:   'Confirmed',
    overridden:  'Overridden',
    auto_booked: 'Auto-booked',
  }[outcome] || outcome;

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Summary strip
// ---------------------------------------------------------------------------

function SummaryStrip({ summary, outcome, onOutcomeChange }) {
  if (!summary) return null;

  // Same toggle semantics as the Board's status cards (BoardTab -> StatCard):
  // clicking an active card clears the filter rather than re-applying it.
  const toggle = (key) => onOutcomeChange(outcome === key ? '' : key);

  return (
    <div className="space-y-4">
      {/* Stat cards — double as outcome filter chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Total Reviewed"
          value={summary.total_rows ?? 0}
          accent="neutral"
          active={outcome === ''}
          onClick={() => onOutcomeChange('')}
          testId="audit-stat-total"
        />
        <StatCard
          label="Confirmed"
          value={summary.confirmed ?? 0}
          accent="success"
          active={outcome === 'confirmed'}
          onClick={() => toggle('confirmed')}
          testId="audit-stat-confirmed"
        />
        <StatCard
          label="Overridden"
          value={summary.overridden ?? 0}
          accent="warn"
          active={outcome === 'overridden'}
          onClick={() => toggle('overridden')}
          testId="audit-stat-overridden"
        />
        <StatCard
          label="Override Rate"
          value={fmtPct(summary.override_rate)}
          accent={summary.override_rate > 0.3 ? 'danger' : 'neutral'}
        />
      </div>

      {/* Confidence averages */}
      <div className="flex flex-wrap gap-4 text-sm text-boss-text-secondary">
        <span>
          Avg confidence <strong className="text-boss-text">confirmed:</strong>{' '}
          {fmtConf(summary.avg_confidence_confirmed)}
        </span>
        <span>
          Avg confidence <strong className="text-boss-text">overridden:</strong>{' '}
          {fmtConf(summary.avg_confidence_overridden)}
        </span>
      </div>

      {/* By-provider table */}
      {summary.by_provider?.length > 0 && (
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-boss-border bg-boss-elevated">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-boss-text-muted">
              By Provider
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-boss-text">
              <thead>
                <tr className="border-b border-boss-border text-xs text-boss-text-secondary uppercase tracking-wide">
                  <th className="px-4 py-2.5 text-left font-medium">Provider</th>
                  <th className="px-4 py-2.5 text-right font-medium">Total</th>
                  <th className="px-4 py-2.5 text-right font-medium">Overridden</th>
                  <th className="px-4 py-2.5 text-right font-medium">Override %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-boss-border">
                {summary.by_provider.map((row) => (
                  <tr key={row.provider} className="hover:bg-boss-elevated transition-colors">
                    <td className="px-4 py-2.5 font-medium">{row.provider || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.total}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.overridden}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(row.override_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By-field table */}
      {summary.by_field?.length > 0 && (
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-boss-border bg-boss-elevated">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-boss-text-muted">
              By Field
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-boss-text">
              <thead>
                <tr className="border-b border-boss-border text-xs text-boss-text-secondary uppercase tracking-wide">
                  <th className="px-4 py-2.5 text-left font-medium">Field</th>
                  <th className="px-4 py-2.5 text-right font-medium">Total</th>
                  <th className="px-4 py-2.5 text-right font-medium">Overridden</th>
                  <th className="px-4 py-2.5 text-right font-medium">Override %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-boss-border">
                {summary.by_field.map((row) => (
                  <tr key={row.field} className="hover:bg-boss-elevated transition-colors">
                    <td className="px-4 py-2.5 font-mono text-[13px]">{row.field || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.total}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.overridden}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(row.override_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar({ filters, onChange }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-xs text-boss-text-secondary mb-1">Session ID</label>
        <input
          className="input-field w-[180px] text-sm"
          placeholder="session_…"
          value={filters.session_id}
          onChange={(e) => onChange({ ...filters, session_id: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs text-boss-text-secondary mb-1">Provider</label>
        <input
          className="input-field w-[140px] text-sm"
          placeholder="Nextiva…"
          value={filters.provider}
          onChange={(e) => onChange({ ...filters, provider: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs text-boss-text-secondary mb-1">Outcome</label>
        <select
          className="input-field w-[140px] text-sm"
          value={filters.outcome}
          onChange={(e) => onChange({ ...filters, outcome: e.target.value })}
        >
          <option value="">All</option>
          <option value="confirmed">Confirmed</option>
          <option value="overridden">Overridden</option>
          <option value="auto_booked">Auto-booked</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-boss-text-secondary mb-1">Date from</label>
        <input
          type="date"
          className="input-field w-[140px] text-sm"
          value={filters.date_from}
          onChange={(e) => onChange({ ...filters, date_from: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-xs text-boss-text-secondary mb-1">Date to</label>
        <input
          type="date"
          className="input-field w-[140px] text-sm"
          value={filters.date_to}
          onChange={(e) => onChange({ ...filters, date_to: e.target.value })}
        />
      </div>
      <button
        type="button"
        onClick={() => onChange({ session_id: '', provider: '', outcome: '', date_from: '', date_to: '' })}
        className="text-xs text-boss-text-muted hover:text-boss-text-secondary transition pb-1"
      >
        Clear
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditTrailTab
// ---------------------------------------------------------------------------

const EMPTY_FILTERS = {
  session_id: '',
  provider:   '',
  outcome:    '',
  date_from:  '',
  date_to:    '',
};

export default function AuditTrailTab() {
  const { hasPermission } = usePermissions();
  // Same perm as the export route (match.row.view — no dedicated export key
  // exists in the MATCH catalog).
  const canExport = hasPermission('match.row.view');

  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [page,       setPage]       = useState(0);
  const [summary,    setSummary]    = useState(null);
  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [summLoading, setSummLoading] = useState(false);
  const [error,      setError]      = useState(null);
  const [exporting,  setExporting]  = useState(false);

  // Request tickets — a response only writes state if it's still the newest.
  const summarySeq = useRef(0);
  const pageSeq    = useRef(0);

  // Filter changes always go back to page 0. One place so it can't be missed.
  const applyFilters = useCallback((next) => {
    setFilters(next);
    setPage(0);
  }, []);

  // Fetch summary whenever filters change
  // Deliberately drops `outcome`: the cards ARE the outcome filter, so the
  // counts must stay stable when one is selected (otherwise picking
  // "Confirmed" would render the "Overridden" card as 0 and you could never
  // switch). Mirrors the Board, whose counters run over the unfiltered rows.
  const loadSummary = useCallback(async (f) => {
    const seq = ++summarySeq.current;
    setSummLoading(true);
    try {
      const res  = await api.get(`${BASE}/summary${buildQs({ ...f, outcome: '' })}`);
      const data = await res.json();
      if (seq !== summarySeq.current) return;   // a newer request superseded this one
      if (res.ok) setSummary(data);
    } catch { /* non-fatal */ } finally {
      // Only the newest request clears the spinner.
      if (seq === summarySeq.current) setSummLoading(false);
    }
  }, []);

  // Fetch one page of items
  const loadPage = useCallback(async (f, p) => {
    const seq = ++pageSeq.current;
    setLoading(true);
    setError(null);
    try {
      const qs  = buildQs(f, { skip: p * PAGE_SIZE, limit: PAGE_SIZE });
      const res  = await api.get(`${BASE}${qs}`);
      const data = await res.json();
      if (seq !== pageSeq.current) return;   // a newer request superseded this one
      if (!res.ok) {
        setError(data?.detail || 'Failed to load audit trail.');
        return;
      }
      setItems(unwrap(data));
      setTotal(typeof data === 'object' && !Array.isArray(data) ? (data.total ?? 0) : 0);
    } catch (err) {
      if (seq !== pageSeq.current) return;   // don't show an error for a dead request
      setError(err?.message || 'Failed to load audit trail.');
    } finally {
      if (seq === pageSeq.current) setLoading(false);
    }
  }, []);

  // The summary query intentionally ignores `outcome` (see loadSummary), so
  // refiring it when only the outcome changes re-fetches byte-identical data.
  // Keying the effect on just the summary-relevant filters skips that entirely
  // — clicking an outcome card now costs one request instead of two.
  //
  // JSON.stringify, not join('|'): the key must be INJECTIVE (distinct inputs ->
  // distinct strings) because React compares deps with Object.is. These values
  // are free text that may contain '|', so join() collides —
  // {session_id:'sess_a|b', provider:''} and {session_id:'sess_a', provider:'b'}
  // both produce "sess_a|b||", React sees no change, and the strip keeps the
  // previous filter's counts while the table updates. stringify quotes and
  // escapes each element so a delimiter inside a value can't be confused with
  // one between values.
  //
  // COUPLING WARNING: loadSummary(filters) sends every field buildQs() knows
  // about, but this effect only re-runs when the fields listed here change. That
  // is correct only while this list covers everything buildQs sends minus
  // `outcome`. Add a filter to buildQs and forget it here -> silently stale
  // counts, no error. Keep the two in sync.
  const summaryKey = JSON.stringify([
    filters.session_id, filters.provider, filters.date_from, filters.date_to,
  ]);

  useEffect(() => {
    loadSummary(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey]);

  // One effect, not two. Two both ran on mount, so every mount fired the same
  // request twice. applyFilters already resets the page, so this covers both.
  useEffect(() => {
    loadPage(filters, page);
  }, [filters, page, loadPage]);

  async function handleExport() {
    setExporting(true);
    try {
      const result = await apiDownloadRich(`${BASE}/export${buildQs(filters)}`);
      if (!result.ok || !result.blob) {
        setError('Export failed.');
        return;
      }
      // Extract filename from Content-Disposition header
      const disposition = result.getHeader('Content-Disposition') || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match ? match[1].replace(/['"]/g, '') : 'audit-trail.csv';

      const url = URL.createObjectURL(result.blob);
      const a   = document.createElement('a');
      a.href    = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Summary strip — the spinner replaces the strip ONLY on first load.
          On a refetch the strip stays mounted and just dims, because swapping
          several hundred px of cards/tables for a one-line spinner collapsed
          the page and made the whole screen jump on every card click. */}
      <div>
        {summLoading && !summary ? (
          <div className="flex items-center gap-2 text-boss-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            Loading summary…
          </div>
        ) : (
          <div className={summLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <SummaryStrip
              summary={summary}
              outcome={filters.outcome}
              onOutcomeChange={(v) => applyFilters((f) => ({ ...f, outcome: v }))}
            />
          </div>
        )}
      </div>

      {/* Filter bar + Export */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <FilterBar filters={filters} onChange={applyFilters} />
        {canExport && (
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium btn-secondary disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {exporting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[var(--boss-danger-soft)] border border-[var(--boss-danger)]/20 text-[var(--boss-danger-text)] text-sm">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Paginated table — min-h so toggling an outcome card doesn't collapse
          the card height and shift everything above it. */}
      <div className="glass-card overflow-hidden min-h-[360px]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-boss-border bg-boss-elevated">
          <span className="text-xs text-boss-text-muted">
            {/* Pagination is by report row (each row can carry a customer AND a
                product decision, so a page may list more lines than rows). */}
            {total} report rows
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="p-1 rounded text-boss-text-muted hover:text-boss-text disabled:opacity-40 transition"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="text-xs text-boss-text-secondary tabular-nums">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="p-1 rounded text-boss-text-muted hover:text-boss-text disabled:opacity-40 transition"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>

        {/* Same rule as the summary strip: only tear the table down when there
            is nothing to show yet. Otherwise a refetch would collapse 50 rows
            into a 160px box and yank the page. */}
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-boss-text-secondary text-sm gap-2">
            <Loader2 size={15} className="animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-boss-text-secondary text-sm">
            No records match the current filters.
          </div>
        ) : (
          <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`}>
            <table className="w-full text-sm text-boss-text">
              <thead>
                <tr className="border-b border-boss-border text-xs text-boss-text-secondary uppercase tracking-wide">
                  <th className="px-4 py-3 text-left font-medium">Provider</th>
                  <th className="px-4 py-3 text-left font-medium">Customer</th>
                  <th className="px-4 py-3 text-left font-medium">Field</th>
                  <th className="px-4 py-3 text-left font-medium">AI Choice</th>
                  <th className="px-4 py-3 text-right font-medium">AI Conf</th>
                  <th className="px-4 py-3 text-left font-medium">Final Choice</th>
                  <th className="px-4 py-3 text-left font-medium">Outcome</th>
                  <th className="px-4 py-3 text-left font-medium">Reviewed By</th>
                  <th className="px-4 py-3 text-left font-medium">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-boss-border">
                {items.map((item, idx) => (
                  <tr
                    key={item.row_id ? `${item.row_id}-${item.field}-${idx}` : idx}
                    className="hover:bg-boss-elevated transition-colors"
                  >
                    <td className="px-4 py-3 text-boss-text-secondary text-xs">
                      {item.provider || '—'}
                    </td>
                    <td className="px-4 py-3 max-w-[180px] truncate font-medium">
                      {item.customer_name || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px] text-boss-text-secondary">
                      {item.field || '—'}
                    </td>
                    <td className="px-4 py-3 text-[13px] max-w-[160px] truncate text-boss-text-muted">
                      {item.ai_choice || '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[13px]">
                      {item.ai_confidence != null ? `${item.ai_confidence}%` : '—'}
                    </td>
                    <td className="px-4 py-3 text-[13px] max-w-[160px] truncate">
                      {item.final_choice || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <OutcomeChip outcome={item.outcome} />
                    </td>
                    <td className="px-4 py-3 text-xs text-boss-text-secondary">
                      {item.reviewed_by || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-boss-text-muted whitespace-nowrap">
                      {fmtDate(item.reviewed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
